import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/*
 * ELIXIVA — Sincronizacion del neceser v3: valida carrito Y contenido visible del drawer.
 * Sin eventos cart:update propios y sin modificar el motor Horizon.
 */
const GIFT_ID = 57621721940352;
const MINIMUM = 5500;

if (!globalThis.__elixivaGiftSafeV3) {
  globalThis.__elixivaGiftSafeV3 = true;

  let scheduled = 0;
  let active = null;
  let needsRecheck = false;
  let generation = 0;
  let retries = 0;
  let checkoutPending = false;
  let allowNativeSubmit = false;
  let drawerObserver = null;
  let observedDrawer = null;

  const url = (path) => {
    const root = globalThis.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root + path : root + '/' + path;
  };

  const sectionIds = () => [...new Set(
    Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((node) => node.getAttribute('data-section-id'))
      .filter(Boolean)
  )];

  const read = async () => {
    const response = await fetch(url('cart.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Lectura del carrito: ' + response.status);
    const cart = await response.json();
    if (!Array.isArray(cart.items) || !Number.isFinite(Number(cart.total_price))) {
      throw new Error('Respuesta de carrito incompleta');
    }
    return cart;
  };

  const write = async (path, data) => {
    const ids = sectionIds();
    if (ids.length) {
      data.sections = ids.join(',');
      data.sections_url = window.location.pathname + window.location.search;
    }
    const response = await fetch(url(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data),
    });
    const payload = await response.json();
    if (!response.ok || payload.status || payload.errors) {
      const error = new Error(payload.description || payload.message || 'Error actualizando el regalo');
      error.isRejection = response.status >= 400 && response.status < 500;
      throw error;
    }
  };

  const giftLines = (cart) => cart.items.filter((item) => Number(item.variant_id) === GIFT_ID);
  const eligible = (cart) => Number(cart.total_price) >= MINIMUM;
  const correct = (cart) => {
    const lines = giftLines(cart);
    return eligible(cart)
      ? lines.length === 1 && Number(lines[0].quantity) === 1
      : lines.length === 0;
  };

  // Contrastar la fila visible con el carrito real. Si el backend ya es
  // correcto, pero el HTML ha quedado viejo tras un morph, también se repara.
  const visibleMismatch = (cart) => {
    const drawerItems = document.querySelector(
      '.cart-drawer__dialog cart-items-component[data-drawer]'
    );
    if (!drawerItems) return false;

    const expectedLines = giftLines(cart);
    const renderedRows = Array.from(
      drawerItems.querySelectorAll('[data-elixiva-gift-line]')
    );
    if (expectedLines.length !== renderedRows.length) return true;

    const expectedKeys = expectedLines.map((item) => String(item.key)).sort();
    const renderedKeys = renderedRows.map((row) => String(row.dataset.key || '')).sort();
    if (expectedKeys.some((key, i) => key !== renderedKeys[i])) return true;

    return expectedLines.some((item) => {
      const row = renderedRows.find((node) => node.dataset.key === String(item.key));
      return !row || Number(row.dataset.elixivaGiftQuantity) !== Number(item.quantity);
    });
  };

  const actionFor = (cart) => {
    const lines = giftLines(cart);
    if (eligible(cart) && lines.length === 0) {
      return ['cart/add.js', { items: [{ id: GIFT_ID, quantity: 1 }] }];
    }
    const updates = {};
    if (!eligible(cart)) {
      lines.forEach((line) => { updates[line.key] = 0; });
    } else if (lines.length > 0) {
      if (Number(lines[0].quantity) !== 1) updates[lines[0].key] = 1;
      lines.slice(1).forEach((line) => { updates[line.key] = 0; });
    }
    return Object.keys(updates).length ? ['cart/update.js', { updates }] : null;
  };

  const render = async (cart) => {
    // HTML recien solicitado: no aplicar sections potencialmente antiguas
    // de un POST que coincidio con un cambio del usuario.
    const ids = sectionIds();
    await Promise.all(ids.map(async (id) => {
      await sectionRenderer.renderSection(id, { cache: false, mode: 'hydration' });
    }));
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') {
        icon.renderCartBubble(Number(cart.item_count) || 0, false, false);
      }
    });
  };

  const drain = () => {
    if (active) {
      needsRecheck = true;
      return active;
    }
    active = (async () => {
      let changed = false;
      for (let pass = 0; pass < 10; pass++) {
        needsRecheck = false;
        const before = generation;
        const cart = await read();
        // Si acaba de terminar otra operacion, no usar la lectura anterior.
        if (needsRecheck || generation !== before) continue;
        const action = actionFor(cart);
        if (action) {
          await write(action[0], action[1]);
          changed = true;
          // Verificar SIEMPRE el estado tras escribir, sin debounce.
          continue;
        }
        if (changed || visibleMismatch(cart)) {
          await render(cart);
          changed = false;
          if (needsRecheck || generation !== before) {
            // Una mutación provocada por nuestro propio render no es una
            // modificación del carrito: volver a LEER, pero no renderizar
            // de nuevo salvo que siga habiendo diferencias visibles.
            continue;
          }
        }
        retries = 0;
        return cart;
      }
      throw new Error('El carrito sigue cambiando; reintentando sincronizar el regalo');
    })().finally(() => {
      active = null;
      if (needsRecheck) schedule(20);
    });
    return active;
  };

  const launch = () => {
    void drain().catch((error) => {
      console.error('[ELIXIVA regalo v2]', error);
      if (!error.isRejection && retries < 2) {
        retries++;
        schedule(retries === 1 ? 150 : 600);
      }
    });
  };

  const schedule = (delay = 30) => {
    generation++;
    needsRecheck = true;
    window.clearTimeout(scheduled);
    scheduled = window.setTimeout(launch, delay);
  };

  // Horizon actualiza el drawer de forma asincrona. Detectamos una
  // sustitucion de sus filas, incluso si el evento cart:update ya paso.
  const watchDrawer = () => {
    const drawer = document.querySelector('cart-drawer-component');
    if (!drawer || observedDrawer === drawer) return;

    if (drawerObserver) drawerObserver.disconnect();
    observedDrawer = drawer;
    drawerObserver = new MutationObserver((records) => {
      const cartChanged = records.some(({ target }) => {
        const el = target.nodeType === 1 ? target : target.parentElement;
        if (!el || typeof el.closest !== 'function') return false;
        return !!el.closest('.cart-drawer__items, cart-items-component[data-drawer]');
      });
      if (cartChanged) schedule(85);
    });
    drawerObserver.observe(drawer, { childList: true, subtree: true });
  };

  const ensureCorrect = async () => {
    for (let attempt = 0; attempt < 4; attempt++) {
      window.clearTimeout(scheduled);
      generation++;
      needsRecheck = true;
      await drain();
      const cart = await read();
      if (correct(cart) && !visibleMismatch(cart)) return cart;
    }
    throw new Error('No se ha podido validar el neceser antes del checkout');
  };

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    watchDrawer();
    if (!event.detail?.data?.didError) schedule(30);
  });
  document.addEventListener(ThemeEvents.discountUpdate, () => schedule(30));

  // Fallback ante eventos perdidos en un navegador: al abrir el drawer,
  // revisar la verdad del servidor una vez despues de abrir y otra tras
  // completarse las respuestas que puedan seguir pendientes.
  document.addEventListener('click', (event) => {
    const target = event.target;
    if (target && typeof target.closest === 'function' &&
        target.closest('[data-testid="cart-drawer-trigger"]')) {
      watchDrawer();
      schedule(120);
      window.setTimeout(() => schedule(30), 700);
    }
  });

  document.addEventListener('submit', (event) => {
    // Safari iOS 15 puede no proporcionar SubmitEvent.submitter.
    // Restringimos el fallback al formulario nativo del drawer.
    const button = event.submitter ||
      (event.target?.id === 'cart-form' ? document.querySelector('.cart-drawer__dialog #checkout') : null);
    if (button?.id !== 'checkout' || !button.closest('.cart-drawer__dialog')) return;
    if (allowNativeSubmit) {
      allowNativeSubmit = false;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending = true;
    button.setAttribute('aria-busy', 'true');

    void (async () => {
      try {
        await ensureCorrect();
        // El morph puede haber sustituido el formulario y el boton.
        const liveButton = document.querySelector('.cart-drawer__dialog #checkout');
        const liveForm = liveButton?.form || document.getElementById('cart-form');
        if (!liveButton || liveButton.disabled || !liveForm) {
          throw new Error('No se encuentra el formulario del checkout');
        }
        // Safari 15 y anteriores no incluyen HTMLFormElement.requestSubmit.
        // click() respeta el botón, su atributo name=checkout, la validación
        // y el evento submit, a diferencia de form.submit().
        allowNativeSubmit = true;
        try {
          if (typeof liveForm.requestSubmit === 'function') {
            liveForm.requestSubmit(liveButton);
          } else {
            liveButton.click();
          }
        } finally {
          allowNativeSubmit = false;
        }
      } catch (error) {
        console.error('[ELIXIVA checkout] No se pudo validar el regalo:', error);
        window.alert('No hemos podido verificar el carrito. Inténtalo otra vez.');
      } finally {
        checkoutPending = false;
        button.removeAttribute('aria-busy');
      }
    })();
  }, true);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) schedule(30);
  });
  window.addEventListener('pageshow', () => {
    watchDrawer();
    schedule(100);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      watchDrawer();
      schedule(100);
    }, { once: true });
  } else {
    watchDrawer();
    schedule(100);
  }
}
